import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { Assessment } from '@app.raytd.com/store';
import { TestSuite } from 'store/src/lib/tests/entity';
import { ReportTestItem } from '../utils/entities';
import useReportViewerContext, { useReportViewContext } from './report-viewer-context';
import { TestItemHeader } from './test-tile-header';
import { TestTileContext, useTestTileContext } from './test-tile/context';
import { NoImages } from '@/app/features/reports/v2/NoImages';
import FastFillText from './test-tile/fast-fill-text';
import TestTileEditTools from './test-tile/edit-toolbar';
import ExpandableContent from './test-tile/expandable-content';
import ExpanderTrigger from './test-tile/expander-trigger';
import { useReportGroupingContext } from '@/app/features/reports/contexts/grouping-context';
import { ImageRow, requiresMissingPhoto } from '@/app/features/reports/v2/ImageGrid';
import TextTile from '@/app/features/reports/components/test-tile/text-with-label';
import Result from '@/app/features/reports/components/test-tile/result';
import TestItemReferenceImages from '@/app/features/testsuites/specifications/TestItemReferenceImages';
import mapSdaComplianceResult from '../utils/mapSdaComplianceResult';
import { PASS_COLOR, FAIL_COLOR } from '../utils/passFailColors';

// Keyed on the raw stored result (mirrors mapSdaComplianceResult's own keys), not the display
// label, so relabeling "Compliant"/"Not Compliant" can't silently desync the color from the label.
const SDA_RESULT_COLOR: Record<string, string> = {
    very_good: PASS_COLOR,
    very_poor: FAIL_COLOR,
};

// Clause/Requirement/Result/Observations/Recommendations column widths — single source of truth
// shared by the tile's own content table and both heading rows (interactive
// SdaResultsColumnHeadings, print SdaPrintColumnHeadings) via <SdaColGroup/>, so all three stay
// pixel-aligned without hand-duplicated width literals (raytd/web#107, raytd/web#153).
//
// This used to be a CSS Grid `grid-cols-[...]` template (SDA_GRID_COLUMNS) with the Requirement
// cell placed via `row-start-1 row-span-2` so its guidance images render beside the other
// columns. print.css (see the comment above `.sda-tile-content-row .text-sm`) documents why that
// doesn't paginate reliably in Chromium print output: a real PDF repro showed Result/
// Observations/Recommendations landing on one page while Requirement's row-span-2 guidance
// images landed on the next, and the only thing that fixed it in the same repro was a native
// HTML <table>/<tr>/<td> structure — the HTML `rowSpan` attribute (needed so Requirement still
// spans both rows) only works on genuine table cells, not a `display: table-cell` div, so this
// is real <table> markup, not the `display: table-header-group` precedent used for the print
// repeating header (PrintableReport.tsx), which never needed a spanning cell. Widths below keep
// the same 0.25 : 2.45 : 0.5 : 1.4 : 1.4 ratio the old grid template used (raytd/web QA 108.03
// items 7-9 — Clause narrow, Requirement widest since it's also where guidance-image text
// renders, Result a short badge, Observations/Recommendations equal), expressed as percentages
// of that ratio's 6.0 total instead of `fr` tracks.
export const SDA_COLUMN_WIDTHS = [
    { key: 'clause', width: '4.1667%' },
    { key: 'requirement', width: '40.8333%' },
    { key: 'result', width: '8.3333%' },
    { key: 'observations', width: '23.3333%' },
    { key: 'recommendations', width: '23.3333%' },
] as const;

// Shared <colgroup> — a width change only ever needs to happen in SDA_COLUMN_WIDTHS above.
export const SdaColGroup: React.FC = () => (
    <colgroup>
        {SDA_COLUMN_WIDTHS.map((column) => (
            <col key={column.key} style={{ width: column.width }} />
        ))}
    </colgroup>
);

// Outer inset only — the old grid's `gap-4`/`px-4` (raytd/web QA 108.03 items 7-9) is split
// across this table-level padding (outer edge, matching every other row's px-4 print:px-2) and
// SDA_CELL_PADDING_CLASSES below (inter-column gap). Deliberately NOT border-spacing: with
// table-layout: fixed, a fixed table's used width is the greater of its declared width and (sum
// of column widths + border-spacing) — since SDA_COLUMN_WIDTHS already sums to 100%, any
// border-spacing on top pushes the table wider than its w-full container instead of shrinking to
// fit (unlike CSS Grid's `gap`, which IS subtracted from `fr`-track sizing before distribution;
// the two are not equivalent despite looking like it). Padding on `<td>`/`<th>` instead stays
// inside each column's declared width (Tailwind preflight sets `box-sizing: border-box`
// globally), so it can't overflow the table this way.
//
// Deliberately does NOT include Tailwind's `table-fixed` utility class, even though every
// consumer needs `table-layout: fixed` (widths come from <colgroup>, not content) — that class
// name literally contains the substring "fixed", which collides with print.css's UI-chrome-hiding
// selector `[class*='fixed']` (hides `position: fixed` toolbars/buttons in print; see print.css
// around the `header, nav, [class*='sticky'], [class*='fixed'] { display: none !important }`
// rule). With `table-fixed` in the class list, print.css was matching and hiding the entire SDA
// content table — found via the real-staging harness (raytd/web#153: local build rendered only
// 2 of 28 items), not a unit test, exactly the kind of thing a component test checking props
// reached a library can't see. `table-layout: fixed` is applied via SDA_TABLE_STYLE (an inline
// style) instead, which print.css's class-based selector can't match.
export const SDA_TABLE_CLASSES = 'w-full border-separate border-spacing-0 px-4 print:px-2';
export const SDA_TABLE_STYLE: React.CSSProperties = { tableLayout: 'fixed' };

// Inter-column gap, applied per-cell (not as table-level border-spacing — see SDA_TABLE_CLASSES'
// comment for why that overflows a fixed table). Shared by the content table's <td>s and both
// heading tables' <th>s so the gap reads identically everywhere.
export const SDA_CELL_PADDING_CLASSES = 'px-2 print:px-1';

// Shared heading-cell className for both SdaResultsColumnHeadings (interactive) and
// SdaPrintColumnHeadings (print) — was hand-duplicated in both files with a `font-light
// font-normal` conflict (both applied to the same <th>; Tailwind emits font-normal's rule after
// font-light's in its generated stylesheet, so font-normal silently won regardless of intent).
// font-normal isn't needed to begin with: an author stylesheet's font-weight utility already
// beats a <th>'s UA-stylesheet bold default regardless of specificity (cascade origin, not
// specificity, decides that), so font-light alone is sufficient.
export const SDA_HEADING_CELL_CLASSES = `text-xs text-zinc-400 font-light text-left align-middle ${SDA_CELL_PADDING_CLASSES}`;

// QA 108.02 (raytd/web#147, items 2+3): print shows EVERY photo. raytd/web#164 had capped print at
// 11 (falling back to ImageRow's "+N" overflow tile) so a photo-heavy tile stayed under one page,
// but QA got a grey "+11" placeholder in place of real photos and asked for them all. Keeping a
// tile within a page is the page-break logic's job (sdaPrintPagination.ts), not a photo cap's.
// Interactive view keeps its own 50-photo cap (scrolling has no page-height constraint).
export const SDA_PRINT_PHOTO_CAP = Infinity;

const TestItemContent: React.FC<{
    item: ReportTestItem;
    testSuite: TestSuite;
    isScrolling: boolean;
    isTileExpanded: boolean;
    handleExpandTile: () => void;
    ratingType: string;
    isPrintView?: boolean;
}> = React.memo(({ item, testSuite, isScrolling, isTileExpanded, handleExpandTile, ratingType, isPrintView = false }) => {

    // isSdaFormat's render-switch axis is the Report being viewed, not the Test Item's own spec
    // type — the same Test Item can appear in either a standard-Format or an SDA-Format report
    // for one Inspection (ADR-0020, raytd/web#84).
    const { viewId, isSdaFormat } = useReportViewContext();
    // [REGRESSION, raytd/web#164] `viewId==='print'` alone misses the Ctrl+P path: ReportView's
    // `hidden print:block` PrintableReport subtree renders TestItem with isPrintView=true but
    // does NOT wrap itself in a fresh ReportViewProvider, so viewId still reads whatever the
    // surrounding interactive page set it to. TestItem's own `shouldHideInteractiveUi` already
    // combines both (`isPrintMode || isPrintView`) — this must too, or the print-only maxPhotos
    // cap (and imageSize) silently never apply on that path.
    const isPrintMode = viewId === 'print' || isPrintView;

    const type = item.testSpecification.type;
    // TestItemReferenceImages (read-only/editable=false) only renders images whose own
    // `included` election is true, and renders null entirely when none qualify — a raw
    // length check can't see that, so both the non-SDA wrapper (raytd/web#108) and the SDA
    // evidence area (which must fully disappear when it would have nothing visible, per
    // raytd/web#99 AC4) check the same election the component itself will apply.
    const hasVisibleGuidanceImages = (item?.referenceImages ?? []).some((img) => img.included);
    const hasPhotos = (item?.images?.length ?? 0) > 0;
    // #99 removed ImageRow's unconditional mount on the SDA branch to keep a photo-less finding
    // compact — but ImageGrid.tsx's own missing-data "photo required" red highlight lives inside
    // ImageRow, so it never mounts either, silently dropping the signal QA relies on in
    // missing-data mode. Read the same missingData/showMissingData pair via useTestTileContext()
    // and the same requiresMissingPhoto() predicate ImageGrid.tsx uses (raytd/web#103), so a
    // future change to the rule can't update one call site and miss the other. Never in print —
    // missing-data mode is an interactive QA tool, and #99 AC4 requires the evidence area to
    // fully disappear in print when there's nothing to show; NoImages below is print:hidden on
    // its own, but its containing <tr> is not, so print must be excluded here, not just there.
    const { missingData, showMissingData } = useTestTileContext();
    const needsPhotoIndicator = !isPrintMode && !hasPhotos && requiresMissingPhoto(missingData, showMissingData);
    // Clause/Requirement/Result span into the photo row only when that row exists — raytd/web#163.
    const showEvidenceRow = hasPhotos || needsPhotoIndicator;
    const evidenceRowSpan = showEvidenceRow ? 2 : undefined;

    return (
        <div className="flex-1">
            {/* Test specification and result section */}
            {isSdaFormat ? (
                // Compact, table-family single row — Clause/Requirement/Result/Observations/
                // Recommendations side by side, at column-width ratios echoing the original
                // SdaComplianceTable's own columns (narrow Cl./Result, wide Requirement/
                // Observations/Recommendations) rather than 5 equal columns, since equal widths
                // squeeze the free-text fields far narrower than that table ever did
                // (raytd/web#95). A real HTML <table> (not CSS Grid — see SDA_COLUMN_WIDTHS'
                // comment, raytd/web#153) so Chromium's print engine paginates reliably.
                // `table-layout: fixed` (SDA_TABLE_STYLE) makes each column shrink-wrap to its
                // <colgroup> width regardless of content, same as the old grid's `minmax(0, Nfr)`
                // did — so a long unbroken token in Requirement/Observations still wraps within
                // its column instead of forcing the row to overflow. Applied via inline style, not
                // Tailwind's `table-fixed` class — see SDA_TABLE_STYLE's comment for why.
                <table
                    data-testid={`sda-tile-content-row-${item.assessmentTestId}`}
                    // mb-6, not mb-4: the row above (pt-4, raytd-app-refactor's report-row-
                    // content.tsx) gives each tile 16px above its content, but this table's own
                    // trailing margin was the only space below the tile's lowest item before the
                    // next tile's border separator — QA said it read noticeably tighter than the
                    // top (raytd/web QA 108.03 refail, item 15). That pt-4 partner is interactive-
                    // view-only (report-row-content.tsx, used by VirtualizedNestedList) — print
                    // tiles compose via PrintableTileWrapper instead, which never applies it, so
                    // print inherited the full mb-6 with nothing above to balance it against,
                    // reading as wasted space before the next grey separator (raytd/web QA
                    // 108.02, item 4). print:mb-2 tightens print specifically; mb-6 still stands
                    // for the interactive view.
                    className={`sda-tile-content-row ${SDA_TABLE_CLASSES} mb-6 print:mb-2`}
                    style={SDA_TABLE_STYLE}
                >
                    <SdaColGroup />
                    <tbody>
                        {/* Column labels (Clause/Requirement/Result/Observations/Recommendations)
                            are hoisted to render once above the results list in the interactive
                            view (SdaResultsColumnHeadings, raytd/web QA 108.03 item 1) and once
                            per printed page in print output (SdaPrintColumnHeadings via a
                            repeating `display: table-header-group` header, raytd/web QA 108.02
                            item 2) instead of repeating on every tile in either view. */}
                        <tr>
                            {/* Clause/Requirement/Result carry rowSpan={2} only when a second
                                (photo) row actually exists below them — raytd/web#163. #153/#159
                                had these three cells (and Requirement's rowSpan candidate) share
                                ONE row with Observations/Recommendations, believing a second `<tr>`
                                was what let Chromium's print engine split a tile across a page
                                boundary. raytd/web#157's own investigation (same epic, #148) found
                                that belief incomplete: it measured IDENTICAL split behaviour across
                                CSS Grid, a real rowSpan table, a two-row table, and a single-row
                                table — row count never affected whether a tile split, only which
                                item split. #157's actual fix (`sdaPrintPagination.ts`,
                                `PrintableReport.tsx`) measures each whole `.printable-tile-wrapper`
                                and forces a break before any tile that won't fit, independent of
                                what's inside it — so the single-row constraint is no longer
                                load-bearing, and this restores the pre-#153 (raytd/web#114)
                                structure. */}
                            <td rowSpan={evidenceRowSpan} className={`align-top ${SDA_CELL_PADDING_CLASSES}`}>
                                <TextTile title="">
                                    {item.testSpecification.label}
                                </TextTile>
                            </td>
                            <td rowSpan={evidenceRowSpan} className={`align-top ${SDA_CELL_PADDING_CLASSES}`}>
                                <TextTile title="" newLines>
                                    {item.testSpecification.description}
                                </TextTile>
                                {hasVisibleGuidanceImages && (
                                    <div className="mt-2" data-testid="sda-evidence-guidance">
                                        <TestItemReferenceImages
                                            testItemId={item.assessmentTestId}
                                            images={item.referenceImages ?? []}
                                            editable={false}
                                            imageSize={isPrintMode ? 'md' : 'sm'}
                                        />
                                    </div>
                                )}
                            </td>
                            <td rowSpan={evidenceRowSpan} className={`align-top ${SDA_CELL_PADDING_CLASSES}`}>
                                <TextTile title="">
                                    <span
                                        className="font-semibold"
                                        style={{ color: item.result ? SDA_RESULT_COLOR[item.result] : undefined }}
                                    >
                                        {mapSdaComplianceResult(item.result)}
                                    </span>
                                </TextTile>
                            </td>
                            <td className={`align-top ${SDA_CELL_PADDING_CLASSES}`}>
                                <FastFillText field_id='observations' label="Observations" item={item} hideLabel />
                            </td>
                            <td className={`align-top ${SDA_CELL_PADDING_CLASSES}`}>
                                <FastFillText field_id='recommendations' label="Recommendations" item={item} hideLabel />
                            </td>
                        </tr>
                        {showEvidenceRow && (
                            <tr>
                                {/* Photos span Observations+Recommendations via a colSpan=2 cell
                                    in this second <tr> — the pre-#153 (raytd/web#114) structure,
                                    restored per raytd/web#163 now that #157 makes row count safe
                                    (see the comment above row 1). Mandatory real-staging pagination
                                    re-verification lives on raytd/web#163's own acceptance
                                    criteria, not here. */}
                                <td colSpan={2} className={`align-top ${SDA_CELL_PADDING_CLASSES}`}>
                                    {hasPhotos ? (
                                        <div className="mt-2" data-testid="sda-evidence-photos">
                                            <ImageRow
                                                media={item?.images ?? []}
                                                imageSize={isPrintMode ? 'md' : 'sm'}
                                                maxPhotos={isPrintMode ? SDA_PRINT_PHOTO_CAP : 50}
                                            />
                                        </div>
                                    ) : (
                                        // #103: no photos, but this finding requires one and
                                        // missing-data mode is on — a compact placeholder (sized
                                        // like the guidance-image side, not the full-width
                                        // non-SDA gallery) so the red "photo required" signal
                                        // still has somewhere to render on the SDA branch.
                                        <NoImages
                                            size={isPrintMode ? 'md' : 'sm'}
                                            message="No photos"
                                            className="rounded-md bg-red-400 text-white print:hidden"
                                            data-testid="sda-missing-photo-indicator"
                                            aria-label="No photos available"
                                        />
                                    )}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            ) : type !== 'generic' && (
                <div className="flex flex-row gap-4 px-4 print:px-2 mb-4">
                    <div className="flex-1 grid grid-cols-2 gap-4">
                        {item.testSpecification.type === 'compliance'
                            ? (
                                <TextTile title="Test Specification" newLines>
                                    {item.testSpecification.description}
                                </TextTile>
                            )
                            : null}
                        {item.testSpecification.type !== 'generic' && (
                            <Result
                                testSuite={testSuite}
                                result={item.result}
                                resultDescription={item.resultDescription}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Main content with solid border — Observations/Recommendations already appear in
                the compact SDA row above, so they don't get a second block here for that format. */}
            {!isSdaFormat && (
            <div className="flex flex-row gap-4 px-4 print:px-2 mb-4">
                <div className="flex-1 grid grid-cols-2 gap-4">
                    <FastFillText field_id='observations' label="Observations" item={item} />
                    <FastFillText field_id='recommendations' label="Recommendations" item={item} />
                </div>
            </div>
            )}

            {/* Expandable content section */}
            {!isScrolling && (

                <ExpandableContent
                    item={item}
                    testSuite={testSuite}
                    isExpanded={isTileExpanded}
                    isSdaFormat={isSdaFormat}
                />
            )}

            {/* SDA's evidence (guidance images under Requirement, photos under Observations+
                Recommendations) now lives inside the merged grid above (raytd/web QA 108.03,
                item 13) rather than as a second, separately-flowing grid block here. */}
            {isSdaFormat ? null : (
                <>
                    {/* Images section */}
                    <div className="px-4 print:px-2 py-2">
                        <div className="min-h-20 flex">
                            <ImageRow
                                className="flex-1"
                                media={item?.images ?? []}
                                imageSize={isPrintMode ? 'md' : 'sm'}
                                maxPhotos={50}
                            />
                        </div>
                    </div>

                    {/* Reference images resolved from the spec (read-only display; election
                        happens in the edit form per ADR-0003) */}
                    {hasVisibleGuidanceImages && (
                        <div className="px-4 print:px-2 py-2">
                            <TestItemReferenceImages
                                testItemId={item.assessmentTestId}
                                images={item.referenceImages ?? []}
                                editable={false}
                                imageSize={isPrintMode ? 'md' : 'sm'}
                            />
                        </div>
                    )}
                </>
            )}

        </div>
    );
});


type TestItemProps = {
    item: ReportTestItem;
    testSuite: TestSuite;
    assessment: Assessment;
    onEdit?: (id: number) => void;
    isPrintView?: boolean;
}

const TestItem: React.FC<TestItemProps> = ({ item, testSuite, assessment, onEdit, isPrintView = false }) => {

    const { missingData, viewId, isSdaFormat } = useReportViewContext();
    const { isScrolling, showMissingData, showFastFillQA, isUpdatingTest, updatingTestId } = useReportViewerContext();
    const { expandedTiles, setExpandedTiles } = useReportGroupingContext();
    const isPrintMode = viewId === 'print';
    const shouldHideInteractiveUi = isPrintMode || isPrintView;
    const [isCommentsOpen, setIsCommentsOpen] = useState(false);

    //console.log('TestItem missingData', missingData);

    // if (isUpdatingTest && updatingTestId === item.assessmentTestId) {
    //     return <TestTileSkeleton />;
    // }

    const handleExpandTile = useCallback(() => {
        if (isPrintView) return;
        setExpandedTiles(prev => {
            const next = new Set(prev);
            if (next.has(item.assessmentTestId)) {
                next.delete(item.assessmentTestId);
            } else {
                next.add(item.assessmentTestId);
            }
            return next;
        });
    }, [isPrintView, item?.assessmentTestId, setExpandedTiles]);

    // SDA tiles have no "show more" trigger to gate behind (raytd/web#106), so they're
    // effectively always expanded, same as print.
    const isTileExpanded = (shouldHideInteractiveUi || isSdaFormat) ? true : expandedTiles.has(item.assessmentTestId);

    useEffect(() => {
        if (!isTileExpanded && isCommentsOpen) {
            setIsCommentsOpen(false);
        }
    }, [isTileExpanded, isCommentsOpen]);

    // useEffect(() => {
    //     if (!isTileExpanded) return;

    //     const element = document.getElementById(`test-item-${item.assessmentTestId}`);
    //     element?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // }, [isTileExpanded, item?.assessmentTestId]);

    const tileValidationErrors = (missingData.get(item.assessmentTestId) || {});
    const hasMissingData = Object.keys(tileValidationErrors).length > 0;
    const exclusionToggleOperable = item.exclusionReason === 'included' || item.exclusionReason === 'manuallyexcluded';

    //console.debug('missing data', item.assessmentTestId, showMissingData, tileValidationErrors, hasMissingData, missingData);

    const contextValues = useMemo(() => ({
        item,
        testSuite,
        missingData: tileValidationErrors, //showMissingData,// && tileValidationErrors,
        hasMissingData: hasMissingData,
        showMissingData: showMissingData,
        exclusionToggleOperable,
        hasFastFillErrors: item.hasFastFillQA, // has errors whether they are approved or not.
        showFastFillQA: showFastFillQA,
        hasUnmatchedSegments: item.hasUnmatchedSegments,
        status: item.status,
        exclusionReason: item.exclusionReason,
        fieldSettings: item.fieldSettings,
        onExpandTile: handleExpandTile,
        isCommentsOpen,
        setIsCommentsOpen
    }), [
        item,
        testSuite,
        showMissingData,
        showFastFillQA,
        tileValidationErrors,
        hasMissingData,
        exclusionToggleOperable,
        handleExpandTile,
        isCommentsOpen,
        setIsCommentsOpen
    ]);

    if (!item) {
        return null;
    }

    return (
        <TestTileContext.Provider value={contextValues}>
            <div
                id={`test-item-${item.assessmentTestId}`}
                className={cn(
                    "flex-1 flex flex-col",
                    isPrintView ? "space-y-4" : "pl-0"
                )}
            >
                {/* Header section with solid border — SDA tiles don't get the ratingType-colored
                    stripe (raytd/web#115), it's not part of the compact SDA table design. */}
                <div className={cn(
                    (isPrintView || isSdaFormat) ? "pl-0" : "border-zinc-200 border-l-2",
                    !isPrintView && !isSdaFormat && {
                        'border-l-compliance-500': item.ratingType.includes('compliance'),
                        'border-l-condition-500': item.ratingType.includes('condition'),
                        'border-l-zinc-500': item.ratingType.includes('generic'),
                    }
                )}>
                    {/* Identity header (location, element, iconography, test number, element
                        id/name) doesn't apply to the compact SDA tile (raytd/web#106) */}
                    {!isSdaFormat && <TestItemHeader item={item} />}


                    {/* Content section with solid border */}
                    <TestItemContent
                        item={item}
                        testSuite={testSuite}
                        isScrolling={isScrolling}
                        isTileExpanded={isTileExpanded}
                        handleExpandTile={handleExpandTile}
                        ratingType={item.ratingType}
                        isPrintView={isPrintView}
                    />
                </div>

                {/* Expander trigger with dashed border - hidden in print view */}
                {!shouldHideInteractiveUi && (
                    <div className={cn(
                        (isPrintView || isSdaFormat) ? "pl-0" : "border-zinc-200 border-l-2",
                        !isPrintView && !isSdaFormat && !isTileExpanded && "border-dashed",
                        !isPrintView && !isSdaFormat && {
                            'border-l-compliance-500': item.ratingType.includes('compliance'),
                            'border-l-condition-500': item.ratingType.includes('condition'),
                            'border-l-zinc-500': item.ratingType.includes('generic'),
                        }
                    )}>
                        {/* "Show more..." has nothing to gate on an SDA tile (raytd/web#106) —
                            the tile is already always-expanded above */}
                        {!isScrolling && !isSdaFormat && (
                            <ExpanderTrigger
                                handleExpandTile={handleExpandTile}
                                ratingType={item.ratingType}
                                isTileExpanded={isTileExpanded}
                            />
                        )}

                        <TestTileEditTools onEdit={onEdit} assessment={assessment} />
                    </div>
                )}
            </div>
        </TestTileContext.Provider>
    );
}


export default TestItem;
